'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { sesionesService } from '@/lib/api/sesionesService';
import { SesionEntry } from '@/types/sesion';
import { Button } from '@/components/ui/Button';
import ErrorMessage from '@/components/common/ErrorMessage';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';

// Normalizar texto para búsqueda (quitar acentos)
const normalizeText = (text: string): string => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

type CriterioFiltro = 'usuario' | 'ingreso' | 'estatus';

const criterios: Array<{ value: CriterioFiltro; label: string }> = [
  { value: 'usuario', label: 'Usuario' },
  { value: 'ingreso', label: 'Hora de Ingreso' },
  { value: 'estatus', label: 'Estatus' },
];

const SesionesManagement: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [sesiones, setSesiones] = useState<SesionEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [criterio, setCriterio] = useState<CriterioFiltro>('usuario');
  const [valor, setValor] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await sesionesService.getAll();
      if (res.data) {
        setSesiones(res.data);
      } else {
        setError(res.error || 'Error al cargar sesiones');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar sesiones');
      console.error('Error fetching sesiones:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated]);

  const filtered = useMemo(() => {
    if (!valor.trim()) return sesiones;
    
    const term = normalizeText(valor.trim());
    
    return sesiones.filter((s) => {
      switch (criterio) {
        case 'usuario':
          return normalizeText(s.usuario || '').includes(term);
        case 'ingreso':
          return normalizeText(s.ingreso || '').includes(term);
        case 'estatus':
          return normalizeText(s.estatus || '').includes(term);
        default:
          return true;
      }
    });
  }, [sesiones, criterio, valor]);

  const formatDateTime = (dateStr: string | undefined): string => {
    if (!dateStr) return '—';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const getEstatusBadge = (estatus: string | undefined): "success" | "danger" | "info" => {
    const est = (estatus || '').toLowerCase();
    if (est === 'activa') return 'success';
    if (est === 'expirada') return 'danger';
    return 'info';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sesiones Activas</h1>
          <p className="text-sm text-gray-500 mt-1">
            Muestra las sesiones activas de usuarios en el sistema. Lista el último ingreso de cada usuario.
          </p>
        </div>
        <Button variant="secondary" onClick={load} disabled={loading}>
          {loading ? "Cargando…" : "Recargar"}
        </Button>
      </div>

      {error && <ErrorMessage message={error} onRetry={load} />}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Criterio</label>
            <select
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={criterio}
              onChange={(e) => setCriterio(e.target.value as CriterioFiltro)}
            >
              {criterios.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              placeholder="Escribe para filtrar..."
            />
          </div>
          <div>
            <Button 
              variant="secondary" 
              onClick={() => setValor('')}
              className="w-full"
            >
              Limpiar
            </Button>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div>
            Mostrando <span className="font-medium text-gray-700">{filtered.length}</span> de{" "}
            <span className="font-medium text-gray-700">{sesiones.length}</span> sesión(es)
          </div>
          <div className="text-gray-400">
            Sesiones expiran después de 60 minutos de inactividad
          </div>
        </div>
      </div>

      {/* Tabla con encabezado fijo */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto max-h-[600px]">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 font-semibold border-b border-gray-200">Usuario</th>
                <th className="text-left p-3 font-semibold border-b border-gray-200">Hora de Ingreso</th>
                <th className="text-left p-3 font-semibold border-b border-gray-200">Estatus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filtered.length === 0 ? (
                <tr>
                  <td className="p-6 text-gray-500 text-center" colSpan={3}>
                    {loading ? 'Cargando sesiones...' : 'No hay sesiones activas o no se encontraron resultados con el filtro aplicado'}
                  </td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="hover:bg-blue-50/40 transition-colors">
                    <td className="p-3 font-medium">{s.usuario || '—'}</td>
                    <td className="p-3 font-mono text-xs">{formatDateTime(s.ingreso)}</td>
                    <td className="p-3">
                      <Badge variant={getEstatusBadge(s.estatus)}>
                        {s.estatus || '—'}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {filtered.length > 0 && (
        <div className="text-right text-xs text-gray-400 px-2">
          Total: {filtered.length} sesión(es) | Horas ajustadas a zona horaria CDMX (UTC-6)
        </div>
      )}
    </div>
  );
};

export default SesionesManagement;
